import Markdown from '@components/V4/Markdown/Markdown';
import { AnnotationIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { SectionHeading } from '@modules/Admin/Reporting/common';
import {
  CoursePlanCommentRef,
  CoursePlanContentDTO,
  CoursePlanDTO,
  CoursePlanPointDTO,
  CoursePlanSourceDTO,
  CoursePlanSourceType,
} from '@shared/apis/coursePlans.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { ReactNode, useState } from 'react';

const SOURCE_STYLES: Record<CoursePlanSourceType, { label: string; className: string }> = {
  agora: { label: 'Agora', className: 'bg-customPrimary/15 text-customPrimary' },
  lms: { label: 'LMS', className: 'bg-customLila/15 text-customLila' },
  transcript: { label: 'Transcripts', className: 'bg-customGold/15 text-customGold' },
  feedback: { label: 'Mentor feedback', className: 'bg-sky-400/15 text-sky-300' },
  web: { label: 'Web', className: 'bg-customGreen/15 text-customGreen' },
  ai: { label: 'AI', className: 'bg-white/10 text-customGrey' },
};

const isUrl = (value: string) => /^https?:\/\//i.test(value);

export const commentRefKey = (ref: CoursePlanCommentRef) =>
  `${ref.section}:${ref.moduleIndex ?? ''}:${ref.lessonIndex ?? ''}`;

export type CommentHooks = {
  onComment?: (ref: CoursePlanCommentRef) => void;
  commentCounts?: Record<string, number>;
};

const CommentButton = (props: { hooks?: CommentHooks; refTarget: CoursePlanCommentRef }) => {
  const { hooks, refTarget } = props;
  if (!hooks?.onComment) return null;
  const count = hooks.commentCounts?.[commentRefKey(refTarget)] ?? 0;
  return (
    <button
      type="button"
      title={`Comment on ${refTarget.label}`}
      onClick={(event) => {
        event.stopPropagation();
        hooks.onComment?.(refTarget);
      }}
      className={mergeClassNames(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors',
        count
          ? 'border-customGold/60 bg-customGold/15 text-customGold'
          : 'border-white/15 text-customGrey hover:border-white/40 hover:text-white'
      )}
    >
      <AnnotationIcon className="h-3.5 w-3.5" />
      {count || 'Comment'}
    </button>
  );
};

export const SourceChips = (props: { sources: CoursePlanSourceDTO[]; compact?: boolean }) => {
  if (!props.sources?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {props.sources.map((source, index) => {
        const style = SOURCE_STYLES[source.type] ?? SOURCE_STYLES.ai;
        const body = (
          <span
            key={index}
            title={`${style.label}${source.ref ? ` · ${source.ref}` : ''}\n${source.note}`}
            className={mergeClassNames(
              'inline-flex max-w-xs cursor-help items-center gap-1 truncate rounded px-1.5 py-px text-[10px] font-semibold',
              style.className
            )}
          >
            {style.label}
            {!props.compact && source.ref && !isUrl(source.ref) ? (
              <span className="truncate font-normal opacity-80">· {source.ref}</span>
            ) : null}
            {isUrl(source.ref) ? <ExternalLinkIcon className="h-3 w-3" /> : null}
          </span>
        );
        return isUrl(source.ref) ? (
          <a key={index} href={source.ref} target="_blank" rel="noreferrer">
            {body}
          </a>
        ) : (
          body
        );
      })}
    </span>
  );
};

const PointList = (props: {
  title: string;
  section: string;
  points: CoursePlanPointDTO[];
  tone?: 'bad' | 'warn';
  hooks?: CommentHooks;
}) => {
  if (!props.points?.length) return null;
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
      <div className="flex items-start justify-between gap-3">
        <h3
          className={mergeClassNames(
            'text-base font-semibold',
            props.tone === 'bad' && 'text-customRed',
            props.tone === 'warn' && 'text-customGold',
            !props.tone && 'text-white'
          )}
        >
          {props.title}
        </h3>
        <CommentButton
          hooks={props.hooks}
          refTarget={{ section: props.section, label: props.title }}
        />
      </div>
      <ul className="flex flex-col gap-2">
        {props.points.map((point, index) => (
          <li key={index} className="flex flex-col gap-1 text-sm text-customLight">
            <span>{point.text}</span>
            <SourceChips sources={point.sources} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const SourceNote = (props: { sources: CoursePlanSourceDTO[] }) =>
  props.sources?.length ? (
    <ul className="flex flex-col gap-0.5 text-xs text-customGrey">
      {props.sources.map((source, index) => (
        <li key={index}>
          <span className="font-semibold">{SOURCE_STYLES[source.type]?.label ?? source.type}</span>
          {source.ref ? (
            isUrl(source.ref) ? (
              <>
                {' · '}
                <a className="underline" href={source.ref} target="_blank" rel="noreferrer">
                  {source.ref}
                </a>
              </>
            ) : (
              ` · ${source.ref}`
            )
          ) : null}
          : {source.note}
        </li>
      ))}
    </ul>
  ) : null;

const Modules = (props: { plan: CoursePlanContentDTO; hooks?: CommentHooks }) => {
  const [open, setOpen] = useState<Set<number>>(new Set([0]));
  const toggle = (index: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <div className="flex flex-col gap-3">
      {props.plan.modules.map((module, index) => (
        <div key={index} className="flex flex-col rounded-2xl bg-surfaceObject">
          <button
            type="button"
            className="flex items-start justify-between gap-4 px-5 py-4 text-left"
            onClick={() => toggle(index)}
          >
            <div className="flex flex-col gap-1">
              <span className="text-base font-semibold text-white">
                <span className="mr-2 text-customGrey">{index + 1}.</span>
                {module.title}
              </span>
              <span className="text-sm text-customGrey">{module.goal}</span>
              <SourceChips sources={module.sources} />
            </div>
            <span className="flex shrink-0 flex-col items-end gap-2">
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs text-customLight">
                {module.sessions} session{module.sessions === 1 ? '' : 's'} ·{' '}
                {module.lessons.length} lesson{module.lessons.length === 1 ? '' : 's'}
              </span>
              <CommentButton
                hooks={props.hooks}
                refTarget={{
                  section: 'modules',
                  moduleIndex: index,
                  label: `Module ${index + 1}: ${module.title}`,
                }}
              />
            </span>
          </button>
          {open.has(index) ? (
            <ol className="flex flex-col border-t border-white/5">
              {module.lessons.map((lesson, lessonIndex) => (
                <li
                  key={lessonIndex}
                  className="flex flex-col gap-2 border-b border-white/5 px-5 py-4 last:border-b-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm font-semibold text-white">
                      {index + 1}.{lessonIndex + 1} {lesson.title}
                    </span>
                    <CommentButton
                      hooks={props.hooks}
                      refTarget={{
                        section: 'modules',
                        moduleIndex: index,
                        lessonIndex,
                        label: `Lesson ${index + 1}.${lessonIndex + 1}: ${lesson.title}`,
                      }}
                    />
                  </div>
                  <p className="text-sm text-customLight">{lesson.description}</p>
                  {lesson.activities?.length ? (
                    <div className="text-sm">
                      <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
                        Activities
                      </span>
                      <ul className="list-disc pl-5 text-customLight">
                        {lesson.activities.map((activity, activityIndex) => (
                          <li key={activityIndex}>{activity}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {lesson.homework ? (
                    <p className="text-sm text-customLight">
                      <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
                        Homework
                      </span>{' '}
                      {lesson.homework}
                    </p>
                  ) : null}
                  {lesson.materials?.length ? (
                    <p className="text-sm text-customLight">
                      <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
                        Materials
                      </span>{' '}
                      {lesson.materials.join(' · ')}
                    </p>
                  ) : null}
                  <SourceNote sources={lesson.sources} />
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const Stat = (props: { label: string; value: ReactNode }) => (
  <div className="flex flex-col rounded-xl bg-white/5 px-4 py-3">
    <span className="text-lg font-semibold text-white">{props.value}</span>
    <span className="text-xs text-customGrey">{props.label}</span>
  </div>
);

export const SourcesPanel = (props: { plan: CoursePlanDTO }) => {
  const sources = props.plan.sources;
  if (!sources) return null;
  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
      <SectionHeading
        title="What this plan was built from"
        description="Counts of the material gathered before writing. Hover any source chip in the plan to see what it contributed."
      />
      <div className="grid grid-cols-2 gap-3 tablet:grid-cols-3 laptop:grid-cols-5">
        <Stat
          label="Agora content"
          value={`${sources.agora.hasDescription ? 'description' : 'no description'}${
            sources.agora.legacyLessons ? `, ${sources.agora.legacyLessons} legacy lessons` : ''
          }`}
        />
        <Stat
          label="LMS units"
          value={
            sources.lms
              ? sources.lms.error
                ? 'unavailable'
                : `${sources.lms.units} in ${sources.lms.sections} sections`
              : 'not linked'
          }
        />
        <Stat
          label="Mentor feedback used"
          value={`${sources.feedback.used} of ${sources.feedback.sessionsWithFeedback}`}
        />
        <Stat
          label="Transcripts read"
          value={`${sources.transcripts.used} of ${sources.transcripts.meetingsWithTranscript}`}
        />
        <Stat
          label="Web sources"
          value={sources.web.error ? 'unavailable' : sources.web.citations.length}
        />
      </div>
      {sources.web.error ? (
        <span className="text-xs text-customGold">Web research failed: {sources.web.error}</span>
      ) : null}
      {sources.web.citations.length ? (
        <ul className="flex flex-col gap-1 text-xs">
          {sources.web.citations.map((citation) => (
            <li key={citation.url}>
              <a
                className="text-customPrimary hover:underline"
                href={citation.url}
                target="_blank"
                rel="noreferrer"
              >
                {citation.title}
              </a>
              <span className="text-customGrey"> · {citation.url}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export const planToMarkdown = (plan: CoursePlanContentDTO) => {
  const sources = (items: CoursePlanSourceDTO[]) =>
    items?.length
      ? `\n  _Sources: ${items
          .map((source) => `${source.type}${source.ref ? ` (${source.ref})` : ''}: ${source.note}`)
          .join('; ')}_`
      : '';
  const points = (title: string, items: CoursePlanPointDTO[]) =>
    items?.length
      ? `\n## ${title}\n${items
          .map((point) => `- ${point.text}${sources(point.sources)}`)
          .join('\n')}\n`
      : '';

  return [
    `# ${plan.title}`,
    '',
    plan.summary,
    '',
    `**Audience:** ${plan.targetAudience}  `,
    `**Duration:** ${plan.durationWeeks} weeks, ${plan.sessionsPlanned} sessions`,
    points('Prerequisites', plan.prerequisites),
    points('Learning outcomes', plan.learningOutcomes),
    '\n## Modules',
    ...plan.modules.map(
      (module, index) =>
        `\n### ${index + 1}. ${module.title} (${module.sessions} sessions)\n${module.goal}${sources(
          module.sources
        )}\n${module.lessons
          .map(
            (lesson, lessonIndex) =>
              `\n#### ${index + 1}.${lessonIndex + 1} ${lesson.title}\n${lesson.description}${
                lesson.activities?.length
                  ? `\n\nActivities:\n${lesson.activities.map((item) => `- ${item}`).join('\n')}`
                  : ''
              }${lesson.homework ? `\n\nHomework: ${lesson.homework}` : ''}${
                lesson.materials?.length ? `\n\nMaterials: ${lesson.materials.join(', ')}` : ''
              }${sources(lesson.sources)}`
          )
          .join('\n')}`
    ),
    points('Assessment', plan.assessment),
    points('Mentor guidelines', plan.mentorGuidelines),
    points('Gaps in current content', plan.gapsInCurrentContent),
    points('Changes versus current course', plan.changesVsCurrent),
  ].join('\n');
};

const PlanView = (props: {
  plan: CoursePlanContentDTO;
  research?: string;
  hooks?: CommentHooks;
}) => {
  const { plan, research, hooks } = props;
  const [showResearch, setShowResearch] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-xl font-bold text-white">{plan.title}</h2>
          <CommentButton hooks={hooks} refTarget={{ section: 'overview', label: 'Overview' }} />
        </div>
        <p className="text-sm text-customLight">{plan.summary}</p>
        <div className="flex flex-wrap gap-4 text-sm text-customGrey">
          <span>
            <span className="font-semibold text-white">Audience:</span> {plan.targetAudience}
          </span>
          <span>
            <span className="font-semibold text-white">Duration:</span> {plan.durationWeeks} weeks ·{' '}
            {plan.sessionsPlanned} sessions
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 laptop:grid-cols-2">
        <PointList
          title="Prerequisites"
          section="prerequisites"
          points={plan.prerequisites}
          hooks={hooks}
        />
        <PointList
          title="Learning outcomes"
          section="learningOutcomes"
          points={plan.learningOutcomes}
          hooks={hooks}
        />
      </div>

      <SectionHeading
        title="Modules and lessons"
        description="Click a module to expand its lessons. Every lesson lists where it came from."
      />
      <Modules plan={plan} hooks={hooks} />

      <div className="grid grid-cols-1 gap-4 laptop:grid-cols-2">
        <PointList title="Assessment" section="assessment" points={plan.assessment} hooks={hooks} />
        <PointList
          title="Mentor guidelines"
          section="mentorGuidelines"
          points={plan.mentorGuidelines}
          hooks={hooks}
        />
        <PointList
          title="Gaps in current content"
          section="gapsInCurrentContent"
          points={plan.gapsInCurrentContent}
          tone="bad"
          hooks={hooks}
        />
        <PointList
          title="Changes versus current course"
          section="changesVsCurrent"
          points={plan.changesVsCurrent}
          tone="warn"
          hooks={hooks}
        />
      </div>

      {research ? (
        <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
          <button
            type="button"
            className="text-left text-sm font-semibold text-white"
            onClick={() => setShowResearch((value) => !value)}
          >
            {showResearch ? 'Hide' : 'Show'} raw web research notes
          </button>
          {showResearch ? <Markdown text={research} className="text-sm" /> : null}
        </div>
      ) : null}
    </div>
  );
};

export default PlanView;
